// Copyright (c) StrangeBee 2023

import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import {
    TaskInput,
    TaskOutput,
    TaskStatus,
    OrgPermissionsEnum,
    ObjectTypeV1 as ObjectType,
    CaseOutput,
} from 'thehive-sdk';
import { useCurrentUser } from '@Queries/userQueries';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import TaskListTitle from '@Organisms/Task/TaskListTitle/TaskListTitle';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import RouteBuilder from '@Services/RouteBuilder';
import client from '@Client';
import { RouteType } from '@Types';
import { actionList } from '@Organisms/Task/actionList';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { IsFlag } from '@Organisms/IsFlag/IsFlag';
import FormatMultipleDate from '@Molecules/Format/FormatMultipleDate/FormatMultipleDate';
import ActionList from '@Molecules/ActionList/ActionList';
import useCasePermissions from '@Hooks/useCasePermissions';
import useConfigList from '@Hooks/useConfigList';
import { computeEventList } from '@Organisms/Task/eventList';
import { useGenericCountQuery } from '@Queries/genericQueries';
import { THLayout } from '@Molecules/THLayout';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import QuickFilterList from '@Molecules/QuickFilterList/QuickFilterList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { SelectedInfo } from '@Atoms/SelectedInfo/SelectedInfo';
import { useListAllFields } from '@Queries/describeQueries';
import { ShortcutPin, usePin } from '@Molecules/Pin';
import { TaskManage } from '@Organisms/Task/TaskManage/TaskManage';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { ICONS } from '@Constants/iconsApp';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { ColumnType } from 'antd/lib/table';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { computeListSelector } from '@Organisms/Task/selector';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { useDrawer } from '@Hooks/useDrawer';
import TaskDrawer from '@Organisms/Task/TaskDrawer/TaskDrawer';
import { MultipleDisplayEntityInfo } from '@Molecules/MultipleDisplayEntityInfo/MultipleDisplayEntityInfo';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Case/selector';
import { IsActionRequired } from '@Organisms/IsActionRequired/IsActionRequired';
import { IsMandatory } from '@Organisms/IsMandatory/IsMandatory';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { ExportButton } from '@Organisms/Export/ExportDrawer/ExportButton';
import usePageTitle from '@Hooks/usePageTitle';

import './TaskList.scss';

const TaskList = (): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const { caseId } = useParams<{ caseId: string }>();
    const { data: describeData } = useListAllFields();

    const { visible, setVisible, data, setData } = useDrawer<TaskOutput>({});

    const pinContext = usePin();

    const config = useConfigList<TaskOutput>({
        selector: computeListSelector({ caseId, describeData: describeData as any }),
        controller: client.TaskController,
        storageId: StorageFilterEnum.TASKS,
        objectType: ObjectType.TASK,
        streamRootId: caseId,
    });

    const { data: currentUser } = useCurrentUser();

    const {
        listRefetch,
        dataSource = [],
        columnsRenderer,
        status = 'loading',
        countRefetch,
        quickFilterListRenderer,
        rowSelectionCustomRenderer,
        stateSelectedKeys: { selectedKeys, setSelectedKeys },
        stateNeedRefresh: { needRefresh, setNeedRefresh },
        storagePageManager,
        storageViewManager,
        filterManager,
        paginationManager,
        table,
        queryParams,
    } = config;

    const { setDefaultDisplay } = storageViewManager || {};

    const {
        page: storagePage,
        setDisplay,
        setListAsGroup,
        setAutoRefresh,
        setOnLoadView,
    } = storagePageManager || { page: null };

    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        startDate: { label: i('common.start-date'), visible: storagePage?.display?.startDate ?? true },
        createdAt: { label: i('common.created-date'), visible: storagePage?.display?.createdAt ?? true },
        updatedAt: { label: i('common.updated-date'), visible: storagePage?.display?.updatedAt ?? true },
    });

    const [localAutoRefresh, setLocalAutoRefresh] = useState(storagePage?.autoRefresh ?? false);
    const [localListAsGroup, setlocalListAsGroup] = useState(storagePage?.listAsGroup ?? false);
    const [exportDrawerVisible, setExportDrawerVisible] = useState(false);

    useDrawer.watch(dataSource, () => {
        if (data) {
            setData(dataSource.find(({ _id }) => _id === data._id));
        }
    });

    setOnLoadView?.((viewContent) => {
        setLocalAutoRefresh(viewContent?.autoRefresh || false);
        setlocalListAsGroup(viewContent?.listAsGroup || false);
        loadVisibleStateDates({
            startDate: viewContent?.display?.startDate ?? true,
            createdAt: viewContent?.display?.createdAt ?? true,
            updatedAt: viewContent?.display?.updatedAt ?? true,
        });
    });

    const { data: caseData } = useOneQuery<CaseOutput>({
        selector: computeGetSelector({ caseId }),
        controller: client.CaseController,
        manual: true,
        fetchOnStart: false,
    });

    const hasPermissions = useCasePermissions({
        caseId,
        permissionsNeeded: [OrgPermissionsEnum.MANAGE_TASK],
    });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.TaskController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (count, message) => {
                    listRefetch();
                    setSelectedKeys([]);
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                },
            }),
        [listRefetch, countRefetch, i, setSelectedKeys],
    );

    const taskActionList = useMemo(
        () => actionList(i, history, caseId, pinContext, caseData?.userPermissions, currentUser),
        [i, history, caseId, pinContext, caseData?.userPermissions, currentUser],
    );

    const getColumns = (disableSort = false): ColumnType<TaskOutput>[] =>
        columnsRenderer([
            {
                className: 'task-list__column-status',
                render: (
                    { flag = false, mandatory = false, extraData },
                    { flag: filterFlag, custom: filterCustom },
                ): ReactElement => {
                    const { actionRequired = false } = extraData || {};
                    return (
                        <div className="task-list____column-status-state">
                            <IsFlag value={flag} onClick={(): void => filterFlag(flag)} />
                            <IsActionRequired value={actionRequired} />
                            <IsMandatory
                                value={mandatory}
                                onClick={(): void => filterCustom({ field: 'mandatory', operator: `${mandatory}` })}
                            />
                        </div>
                    );
                },
            },
            {
                title: [
                    { label: i('task.list.table.columns.task'), sortField: 'title', disableSort },
                    { label: i('task.list.table.columns.order'), sortField: 'order', disableSort },
                ],
                className: 'task-list__column-title column_preview',
                render: (task, { date: filterDate, custom: filterCustom, status: filterStatus }) => (
                    <TaskListTitle
                        task={task}
                        caseId={caseId}
                        onDueDateClick={(dueDate) => filterDate(dueDate, 'dueDate')}
                        onGroupClick={(group) => filterCustom({ field: 'group', operator: 'any', values: [group] })}
                        onStatusClick={(status) => filterStatus(status)}
                        onPreviewClick={() => {
                            setVisible(true);
                            setData(task);
                        }}
                    />
                ),
            },
            {
                title: i('case.list.table.columns.details'),
                className: 'case-list__column-details',
                render: ({ extraData }): ReactElement => (
                    <MultipleDisplayEntityInfo
                        className="case-list__details"
                        data={[
                            {
                                label: i('task.logs.title'),
                                count: extraData?.logCount || 0,
                            },
                            {
                                label: i('sharing.title'),
                                count: extraData?.shareCount || 0,
                                hide: !organisations.some(({ links }) => links.length),
                            },
                        ]}
                    />
                ),
            },
            {
                title: [{ label: i('task.list.table.columns.assignee'), sortField: 'assignee', disableSort }],
                className: 'task-list__column-assignee',
                render: ({ assignee }): ReactElement => <AssigneeInfo assignee={assignee} avatarOnly />,
            },
            {
                title: [
                    { label: i('common.dates') },
                    { label: 'S.', sortField: 'startDate', disableSort, hide: !showDates.startDate },
                    { label: 'C.', sortField: '_createdAt', disableSort, hide: !showDates.createdAt },
                    { label: 'U.', sortField: '_updatedAt', disableSort, hide: !showDates.updatedAt },
                ],
                optionalRender: DisplayDates,
                className: 'task-list__column-date',
                render: ({ _createdAt = 0, startDate = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                    <FormatMultipleDate
                        dates={[
                            {
                                label: 'S.',
                                value: startDate,
                                onClick: (): void => filterDate(startDate, 'startDate'),
                                hide: !showDates.startDate,
                            },
                            {
                                label: 'C.',
                                value: _createdAt,
                                onClick: (): void => filterDate(_createdAt, '_createdAt'),
                                hide: !showDates.createdAt,
                            },
                            {
                                label: 'U.',
                                value: _updatedAt,
                                onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                                hide: !showDates.updatedAt,
                            },
                        ]}
                    />
                ),
            },
            {
                className: 'task-list__column-actions',
                render: (task) => (
                    <ActionsList
                        actionList={taskActionList([task], (count) => {
                            listRefetch();
                            count && countRefetch();
                        })}
                        scope="single"
                        isIcon
                    />
                ),
            },
        ]);

    const { login = '', organisations = [] } = currentUser || {};

    const quickFilterList = quickFilterListRenderer([
        {
            title: i('filter.quick.task.my'),
            onClick: ({ assignee }): void => assignee(login),
        },
        { divider: true },
        {
            title: i('filter.quick.task.in-progress'),
            onClick: ({ status }): void => status(TaskStatus.IN_PROGRESS),
        },
        {
            title: i('filter.quick.task.waiting'),
            onClick: ({ status }): void => status(TaskStatus.WAITING),
        },
        {
            title: i('filter.quick.task.completed'),
            onClick: ({ status }): void => status(TaskStatus.COMPLETED),
        },
        {
            title: i('filter.quick.task.canceled'),
            onClick: ({ status }): void => status(TaskStatus.CANCEL),
        },
        { divider: true },
        {
            title: i('filter.quick.task.flagged'),
            onClick: ({ flag }): void => flag(),
        },
        { divider: true },
        {
            title: i('filter.quick.task.unassigned'),
            onClick: ({ assignee }): void => assignee(),
        },
    ]);

    const rowSelection = rowSelectionCustomRenderer({
        label: i('common.selected'),
    });

    const { data: describe } = useListAllFields();

    const taskList = useMemo(
        () =>
            localListAsGroup
                ? dataSource.reduce((acc, curr) => {
                      const currentGroup = acc[curr.group] || [];

                      return { ...acc, [curr.group]: [...currentGroup, curr] };
                  }, {} as { [key: string]: TaskOutput[] })
                : dataSource,
        [localListAsGroup, dataSource],
    );

    useEffect(() => {
        setDisplay?.('startDate', showDates.startDate);
        setDisplay?.('createdAt', showDates.createdAt);
        setDisplay?.('updatedAt', showDates.updatedAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDates]);

    useEffect(() => {
        const currentView = storagePage?.currentView || '';

        setDefaultDisplay?.(currentView, {
            startDate: showDates.startDate,
            createdAt: showDates.createdAt,
            updatedAt: showDates.updatedAt,
        });
    }, [storagePage, showDates, setDefaultDisplay]);

    useEffect(() => {
        setAutoRefresh?.(localAutoRefresh);
    }, [localAutoRefresh]); // eslint-disable-line

    useEffect(() => {
        setListAsGroup?.(localListAsGroup);
    }, [localListAsGroup]); // eslint-disable-line

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.CASE },
        (data): void => {
            const needUpdate = data.some(({ base }) => {
                const { action, details } = base;
                return (
                    action === 'update' &&
                    Object.keys(details).includes('status') &&
                    base._createdBy !== currentUser?.login
                );
            });

            if (needUpdate) {
                countRefetch();
                listRefetch();
            }
        },
        [currentUser?.login],
    );

    useStreamObserver({ rootId: caseId, objectType: STREAM_EVENTS.LOG }, (): void => {
        listRefetch();
        countRefetch();
    });

    usePageTitle(
        i('page-title.cases.task-list', {
            count: dataSource.filter((item) => item.status === TaskStatus.WAITING).length,
            number: caseData?.number || 0,
        }),
    );

    return (
        <THLayout>
            <THLayout.Content>
                <>
                    <THList>
                        <THList.Header>
                            <THList.Slot name="MANAGE">
                                {hasPermissions && (
                                    <TaskManage
                                        onFinish={(task): Promise<any> => {
                                            return client.TaskController.create<TaskInput, TaskOutput>(task, caseId)
                                                .then(() => {
                                                    listRefetch();
                                                    countRefetch();
                                                    openMessage('success', i('notification.task.create'));
                                                })
                                                .catch(handleNotificationError);
                                        }}
                                        triggerElem={<AddButton label={i('task.list.sider.add-task')} />}
                                    />
                                )}
                                {storagePageManager && storageViewManager ? (
                                    <ManageViewList
                                        pageManager={storagePageManager}
                                        viewManager={storageViewManager}
                                        beforeCreate={(content: any): any => {
                                            filterManager.applyFilters();
                                            return { ...content, filters: filterManager.tempFilters };
                                        }}
                                    />
                                ) : null}
                                <QuickFilterList quickFilterList={quickFilterList} />

                                <ExportButton
                                    onClick={() => {
                                        setExportDrawerVisible(true);
                                    }}
                                    objectType="Task"
                                    selectedItems={dataSource.reduce((acc: string[], data) => {
                                        if (selectedKeys.includes(data['_id'])) {
                                            acc.push(data['_id']);
                                        }
                                        return acc;
                                    }, [])}
                                    queryParams={queryParams}
                                    visible={exportDrawerVisible}
                                    afterVisible={setExportDrawerVisible}
                                    listNumber={table.count}
                                />

                                <SelectedInfo count={selectedKeys?.length} label={i('common.selected')} />
                                <ActionList
                                    actions={taskActionList(
                                        dataSource.filter((data: { _id: string }) =>
                                            selectedKeys.includes(data['_id']),
                                        ),
                                        (count) => {
                                            listRefetch?.();
                                            table?.setSelectedKeys?.([]);
                                            if (count) {
                                                countRefetch?.();
                                            }
                                        },
                                    )}
                                    scope="list"
                                    hidden={!selectedKeys?.length}
                                />
                            </THList.Slot>

                            <THList.Slot name="NAVIGATION">
                                {!localAutoRefresh && (
                                    <THTooltip
                                        title={needRefresh ? i('common.refresh') : i('common.update-list')}
                                        placement="bottom"
                                    >
                                        <THButton
                                            size="small"
                                            type={needRefresh ? 'primary' : 'default'}
                                            shape="circle"
                                            onClick={() => {
                                                listRefetch();
                                                countRefetch();
                                                setNeedRefresh(false);
                                            }}
                                        >
                                            <ICONS.SyncOutlined />
                                        </THButton>
                                    </THTooltip>
                                )}
                                <ManageSwitch
                                    label={i('common.auto-refresh')}
                                    value={localAutoRefresh}
                                    onChange={(autoRefresh): void => setLocalAutoRefresh(autoRefresh)}
                                    icon={<ICONS.SyncOutlined />}
                                />

                                <ManageSwitch
                                    label={i('task.list.table.group.show-group')}
                                    value={localListAsGroup}
                                    onChange={(): void => {
                                        setlocalListAsGroup(!localListAsGroup || false);
                                    }}
                                    icon={<ICONS.GroupOutlined />}
                                />

                                <ManageSwitch
                                    label={i('common.filters')}
                                    value={filterManager.editFilters}
                                    onChange={(editFilters): void => {
                                        filterManager.setEditFilters(editFilters);
                                    }}
                                    icon={<ICONS.FilterOutlined />}
                                />
                            </THList.Slot>

                            <THList.Slot name="CONTENT">
                                {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                                    <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.TASK]}>
                                        <Footer filterConfig={filterManager} />
                                    </THFilter>
                                )}
                            </THList.Slot>
                        </THList.Header>

                        {Array.isArray(taskList) ? (
                            <THList.Content
                                dataSource={taskList}
                                columns={getColumns()}
                                rowKey="_id"
                                rowSelection={hasPermissions ? rowSelection : undefined}
                                emptyPlaceholder={
                                    <DisplayEmptyData spin={status === 'loading'} label={i('task.none')}>
                                        {hasPermissions && (
                                            <TaskManage
                                                onFinish={(task): Promise<any> => {
                                                    return client.TaskController.create<TaskInput, TaskOutput>(
                                                        task,
                                                        caseId,
                                                    )
                                                        .then(() => {
                                                            listRefetch();
                                                            countRefetch();
                                                            openMessage('success', i('notification.task.create'));
                                                        })
                                                        .catch(handleNotificationError);
                                                }}
                                                triggerElem={
                                                    <THButton type="link">{i('task.list.sider.add-task')}</THButton>
                                                }
                                            />
                                        )}
                                    </DisplayEmptyData>
                                }
                            />
                        ) : (
                            <div className="task-list__group-content">
                                {Object.entries(taskList).map(
                                    ([name, data]): ReactElement => (
                                        <THList.Content
                                            key={name}
                                            name={name}
                                            dataSource={data}
                                            columns={getColumns(true)}
                                            rowKey="_id"
                                            rowSelection={hasPermissions ? rowSelection : undefined}
                                            emptyPlaceholder={
                                                <DisplayEmptyData spin={status === 'loading'} label={i('task.none')}>
                                                    {hasPermissions && (
                                                        <TaskManage
                                                            onFinish={(task): Promise<any> => {
                                                                return client.TaskController.create<
                                                                    TaskInput,
                                                                    TaskOutput
                                                                >(task, caseId)
                                                                    .then(() => {
                                                                        listRefetch();
                                                                        countRefetch();
                                                                        openMessage(
                                                                            'success',
                                                                            i('notification.task.create'),
                                                                        );
                                                                    })
                                                                    .catch(handleNotificationError);
                                                            }}
                                                            triggerElem={
                                                                <THButton type="link">
                                                                    {i('task.list.sider.add-task')}
                                                                </THButton>
                                                            }
                                                        />
                                                    )}
                                                </DisplayEmptyData>
                                            }
                                        />
                                    ),
                                )}
                            </div>
                        )}

                        <THList.Footer>
                            <THList.Slot name="NAVIGATION">
                                <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                                <PaginationSize paginationConfig={paginationManager} />
                            </THList.Slot>
                        </THList.Footer>
                    </THList>

                    {data && visible && (
                        <TaskDrawer
                            visible={visible}
                            onVisibleChange={setVisible}
                            data={data}
                            caseId={caseId}
                            eventList={eventList}
                            actionList={taskActionList}
                            hasPermissions={hasPermissions}
                        />
                    )}
                </>
            </THLayout.Content>
        </THLayout>
    );
};

const MenuCount = (): ReactElement => {
    const { caseId } = useParams<{ caseId: string }>(); //! it's not beautiful (sorry, it's just for this step)
    const local = useLocalMessage();
    const history = useHistory();
    const { data: describeData } = useListAllFields();

    const { data: count = 0, refetch: countRefetch } = useGenericCountQuery({
        controller: client.CaseController,
        queryParams: {
            selector: {
                ...computeListSelector({ caseId, describeData: describeData as any }),
                name: `count-case-tasks-${caseId}`,
            },
        },
        fetchOnStart: true,
    });

    useStreamObserver({ rootId: caseId, objectType: STREAM_EVENTS.TASK }, (): void => {
        countRefetch();
    });

    useStreamObserver({ rootId: caseId, objectType: STREAM_EVENTS.CASE }, (data): void => {
        const needUpdate = data.some(({ base }) => {
            const { action, details } = base;
            return action === 'update' && Object.keys(details).includes('status');
        });

        if (needUpdate) {
            countRefetch();
        }
    });

    const processedCount = count < 0 ? `+${Math.abs(count)}` : count;

    return (
        <>
            <ICONS.TaskIcon />
            <span className="tab-counter__label">
                {local('case.layout.nav-bar.tasks')} ({processedCount})
            </span>
            <ShortcutPin caseId={caseId} type="tasks" onClick={({ url }: { url: string }): void => history.push(url)} />
        </>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.tasks',
        render: <MenuCount />,
    },
    path: RouteBuilder.compute('cases.tasks'),
    content: <TaskList />,
    exact: true,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.tasks' },
};
